import { type MouseEvent, type ReactNode } from 'react';
import { Kbd } from '@optiaxiom/react';

import { NamespacedLink } from '@frontend/components/ui';
import { getShortcutModifier } from '@frontend/utils/platform';

import { type TActionRenderProp, type TType } from '../store';

import * as styles from './commands.css';

/** `data` shape shared by the `setting` and `theme` render types. */
export type CommandData = { title: string; icon: ReactNode };

/** `data` for the `navigation` type — a real link plus its click handler. */
export type NavCommandData = CommandData & {
  to: string;
  onClick: (e: MouseEvent) => void;
};

const rowRender: TActionRenderProp = ({ data }) => {
  const { icon, title } = data as CommandData;
  return (
    <span className={styles.row}>
      {icon}
      <span>{title}</span>
    </span>
  );
};

const navigationRender: TActionRenderProp = ({ data, focused }) => {
  const { icon, onClick, title, to } = data as NavCommandData;
  return (
    // tabIndex -1: the palette is a combobox with virtual focus (aria-activedescendant),
    // so nav rows must stay out of the Tab order like every other row type — the anchor
    // remains a real link for mouse Cmd/middle-click new-tab and right-click semantics.
    <NamespacedLink className={styles.rowLink} onClick={onClick} tabIndex={-1} to={to}>
      {icon}
      <span>{title}</span>
      {focused && (
        // Surfaces the existing Cmd/Ctrl+Enter → new-tab shortcut on the active row.
        // aria-hidden: a visual affordance only — keep it out of the link's accessible name.
        <span aria-hidden className={styles.navHint}>
          <Kbd>{getShortcutModifier()}</Kbd>
          <span className={styles.navHintDim}>+</span>
          <Kbd>↵</Kbd>
          <span className={styles.navHintDim}>&nbsp;— New Tab</span>
        </span>
      )}
    </NamespacedLink>
  );
};

/** Render strategies registered globally, keyed by type: plain rows for settings/theme, a real link for navigation. */
export const SPOTLIGHT_TYPES: Record<string, TType> = {
  setting: { key: 'setting', actionItemRender: rowRender },
  theme: { key: 'theme', actionItemRender: rowRender },
  navigation: { key: 'navigation', actionItemRender: navigationRender },
};
